import './style.scss';

import { useEffect, useMemo, useRef, useState } from 'react';

import { getUserData, type ResultType } from '../../../constants/global';
import { useGlobalContext } from '../../../context/global';
import { HvcLoad, useCopy } from '../../utils/hooks';
import { CheckSVG, ShareSVG, UploadSVG } from '../../utils/svgs';
import Lightbox from '../child-agent/Lightbox';
import { defaultResultState } from '../view-submited-result/helpers';
import { useGetResultsByRefID } from '../view-submited-result/use-get-result-by-ref-id';
import Certificate from './Certificate';
import { downloadBlob, exportPdf, exportPng, receiptFilename, shareReceipt, urlToDataUrl } from './export';
import { buildReceiptModel } from './model';
import Seal from './Seal';

const MAX_SHEETS = 6;

type ExportKind = 'pdf' | 'png' | 'share';

const CompletionReceipt = () => {
	const { rsProps, states, actions } = useGlobalContext();

	const data = (rsProps?.data ?? {}) as {
		referenceId?: string;
		electionName?: string;
		assignment?: { code?: string; name?: string };
	};

	const user = getUserData().user;
	const official = states?._election?.get_ElectionOfficial?.data?.find((i) => i.userId === Number(user?.UserId));
	const referenceId = data.referenceId || official?.referenceId || '';
	const resultType = (rsProps?.resultType ?? official?.assignment.resultType?.toUpperCase()) as ResultType;
	const assignment = data.assignment || official?.assignment;
	// The reducer stores the response BODY, so the payload is under `.data`.
	const profile = states?._auth?.getProfile_Auth?.data;

	// The panel renders whenever it is handed a reference + level; the entry points
	// (overview card, sidebar) own the "is this assignment completed?" gating. This
	// also lets the post-submission success screen open it before get_ElectionOfficial
	// has been refetched with the new isCompleted flag.
	const canShow = !!referenceId && !!resultType;

	const captureRef = useRef<HTMLDivElement>(null);
	const copyProps = useCopy();

	const [resultLoading, setResultLoading] = useState(canShow);
	const [sheetImages, setSheetImages] = useState<(string | null)[]>([]);
	const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
	const [busy, setBusy] = useState<ExportKind | null>(null);
	const [lightbox, setLightbox] = useState<number | null>(null);

	const resultByRefId = useGetResultsByRefID({
		rstype: resultType,
		// Whether the result loads or comes back empty (read-store lag) or fails, the
		// certificate still certifies the submission from the assignment — the model
		// degrades to "figures pending". So both paths just clear the loader.
		onSuccess: () => setResultLoading(false),
		onFailure: () => setResultLoading(false),
	});

	// Best-effort enrichment: party name + verified full name live on the profile,
	// not the JWT. Fetch it once if it isn't already in state.
	useEffect(() => {
		if (canShow && !profile) actions?.getProfile_Auth({});
	}, [canShow]);

	useEffect(() => {
		if (!canShow) return;
		setResultLoading(true);
		resultByRefId.getResults(referenceId);
	}, [referenceId, canShow]);

	const result = useMemo(() => resultByRefId.response || defaultResultState, [resultByRefId.response]);

	const model = useMemo(
		() => buildReceiptModel({ result, official, profile, user, resultType, referenceId, assignment, now: new Date() }),
		[result, official, profile, resultType, referenceId, assignment]
	);

	// Once loading settles, generate the QR (locally → data-URL, no network) and
	// inline the result-sheet images so the html2canvas capture stays taint-free.
	useEffect(() => {
		if (resultLoading) return;
		let cancelled = false;

		import('qrcode')
			.then((QR) =>
				QR.toDataURL(model.verification.url, {
					margin: 1,
					width: 240,
					color: { dark: '#022745', light: '#ffffff' },
				})
			)
			.then((url) => !cancelled && setQrDataUrl(url))
			.catch(() => !cancelled && setQrDataUrl(null));

		Promise.all(model.documentUrls.slice(0, MAX_SHEETS).map(urlToDataUrl)).then((imgs) => !cancelled && setSheetImages(imgs));

		return () => {
			cancelled = true;
		};
	}, [resultLoading, model.referenceId]);

	const onCopyReference = () => {
		copyProps.setAction('ref');
		copyProps.copy(model.referenceId);
	};
	const referenceCopied = copyProps.copySuccess && copyProps.action === 'ref';

	const runExport = async (kind: ExportKind) => {
		if (!captureRef.current || busy) return;
		setBusy(kind);
		try {
			const filename = receiptFilename(model.referenceId, kind === 'pdf' ? 'pdf' : 'png');
			if (kind === 'pdf') {
				const blob = await exportPdf(captureRef.current);
				downloadBlob(blob, filename);
				actions?.notificationGlobal('Certificate downloaded.', true);
			} else if (kind === 'png') {
				const blob = await exportPng(captureRef.current);
				downloadBlob(blob, filename);
				actions?.notificationGlobal('Certificate image saved.', true);
			} else {
				const blob = await exportPng(captureRef.current);
				const outcome = await shareReceipt(
					blob,
					filename,
					'iElect Certificate of Submission',
					`Result submission certificate — ${model.electionName} — Ref ${model.referenceId}`
				);
				if (outcome === 'downloaded') {
					copyProps.setAction('share');
					copyProps.copy(model.verification.url);
					actions?.notificationGlobal('Sharing not supported here — image saved and verify link copied.', true);
				}
			}
		} catch {
			actions?.notificationGlobal('Could not generate the certificate. Please try again.', false);
		} finally {
			setBusy(null);
		}
	};

	// ── Locked / guard state ──
	if (!canShow) {
		return (
			<div className='completion-receipt completion-receipt--locked'>
				<div className='completion-receipt__locked-seal'>
					<Seal
						size={96}
						verified={false}
					/>
				</div>
				<h3>No certificate yet</h3>
				<p>Your certificate of submission appears here once you have completed and submitted your assigned result.</p>
			</div>
		);
	}

	return (
		<div className='completion-receipt'>
			<HvcLoad
				load={resultLoading}
				loadTxt='Preparing your certificate…'
				view
			>
				<Certificate
					ref={captureRef}
					model={model}
					sheetImages={sheetImages}
					qrDataUrl={qrDataUrl}
					onCopyReference={onCopyReference}
					referenceCopied={referenceCopied}
					onThumbClick={(i) => setLightbox(i)}
				/>

				<div className='completion-receipt__actions'>
					<button
						type='button'
						className='completion-receipt__action completion-receipt__action--primary'
						onClick={() => runExport('pdf')}
						disabled={!!busy}
					>
						{busy === 'pdf' ? <span className='completion-receipt__spinner' /> : <UploadSVG color='currentColor' />}
						{busy === 'pdf' ? 'Preparing…' : 'Download PDF'}
					</button>
					<button
						type='button'
						className='completion-receipt__action'
						onClick={() => runExport('share')}
						disabled={!!busy}
					>
						{busy === 'share' ? <span className='completion-receipt__spinner' /> : <ShareSVG />}
						Share
					</button>
					<button
						type='button'
						className='completion-receipt__action'
						onClick={() => runExport('png')}
						disabled={!!busy}
					>
						{busy === 'png' ? <span className='completion-receipt__spinner' /> : <CheckSVG color='currentColor' />}
						Save image
					</button>
				</div>
			</HvcLoad>

			{lightbox !== null ? (
				<Lightbox
					images={model.documentUrls}
					startIndex={lightbox}
					label='Result sheet'
					onClose={() => setLightbox(null)}
				/>
			) : null}
		</div>
	);
};

export default CompletionReceipt;
